// Shared overtime helpers for the time loggers (QuickTimeLog + My Time).
//
// The daily cap defaults to a person's capacity_hours_per_day (typically 7.5).
// Anything logged beyond the cap on a given day is treated as overtime and, for
// eligible staff, can be split off into its own entry + a pending overtime request.

export const DEFAULT_DAILY_CAP = 7.5;

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The daily cap for a user, falling back to the default. */
export function dailyCap(user) {
  const c = parseFloat(user?.capacity_hours_per_day);
  return c > 0 ? c : DEFAULT_DAILY_CAP;
}

/**
 * Hours worked in a set of entries: everything logged except the entries
 * approved leave writes into the log (they carry leave_request_id). A day's
 * cap, a short day and the overtime split all measure work, and the leave is
 * already taken off what is expected, so counting it again would make a half
 * day off look like a full day worked.
 */
export function workedHours(entries) {
  return (entries || []).reduce((s, e) => s + (e?.leave_request_id ? 0 : (parseFloat(e?.hours) || 0)), 0);
}

/** Whether the user is allowed to claim overtime (defaults to true). */
export function canClaimOvertime(user) {
  return user?.can_claim_overtime !== 0;
}

/**
 * Given the hours already logged on a day, the hours being added, and the cap,
 * split the addition into a normal part (up to the cap) and an overtime part
 * (the remainder over the cap).
 *
 * @returns {{ normalPart: number, overtimePart: number, overCap: boolean }}
 */
export function splitOvertime(alreadyToday, added, cap) {
  const a = parseFloat(alreadyToday) || 0;
  const h = parseFloat(added) || 0;
  const remaining = Math.max(0, cap - a);
  const normalPart = Math.min(h, remaining);
  const overtimePart = Math.max(0, h - remaining);
  return { normalPart: round2(normalPart), overtimePart: round2(overtimePart), overCap: overtimePart > 0 };
}

/**
 * A duration in decimal hours, written the way people say it: 3h 15m, 45m, 7h.
 *
 * Overtime rows are stored as decimal hours because that is what the policy
 * multiplies, but 0.33h on screen is twenty minutes in disguise. Rounding to
 * the nearest minute is deliberate: the stored value is already rounded to two
 * decimals, so 3.67h is 220.2 minutes and reads as 3h 40m.
 *
 * Nothing and zero come back as an em dash, so an empty cell reads as empty
 * rather than as a worked-but-tiny figure.
 */
export function formatHoursMinutes(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  const total = Math.round(Math.abs(n) * 60);
  if (total === 0) return '—';
  const sign = n < 0 ? '-' : '';
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${sign}${mins}m`;
  return mins === 0 ? `${sign}${hours}h` : `${sign}${hours}h ${mins}m`;
}

// ── Rates are not here ──────────────────────────────────────────────
//
// This module once exported the rate vocabulary a manager picked from, because
// two screens asked for one. Neither does now: the rate is the policy's, read
// off the date worked, and it is shown on the finance Overtime page alone. The
// label for each kind lives with that page, in OvertimeLedgerPage's
// KIND_LABELS. Nothing a manager or a member of staff sees prices overtime, so
// there is deliberately nothing here for such a screen to import.
